import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { GlobeIcon, LayersIcon, PlusIcon, ShieldCheckIcon } from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { PermissionGate } from '@/components/permission-gate';
import { StatusChip } from '@/components/status-chip';
import { MaskedKey } from '@/components/sites/masked-key';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { messageForError } from '@/lib/api-client';
import { siteDetailQuery, siteKeys, sitesApi, sitesQuery } from '@/lib/api/sites';
import type { SiteDetail, SiteEnvironment, SiteWithEnvironments } from '@/lib/api/types';
import { formatDate } from '@/lib/utils';

const createSchema = z.object({
  name: z.string().min(1, 'Give the site a name').max(200),
  slug: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers and hyphens')
    .max(80)
    .optional()
    .or(z.literal('')),
  description: z.string().max(1000).optional(),
  baseUrl: z.string().max(2048).optional(),
  createStagingEnvironment: z.boolean(),
});
type CreateValues = z.infer<typeof createSchema>;

const ENV_TONE = {
  production: 'active',
  staging: 'info',
  development: 'neutral',
} as const;

function EnvironmentRow({ environment }: { environment: SiteEnvironment }) {
  return (
    <div className="flex flex-col gap-2 border-b border-hairline py-3 last:border-b-0 last:pb-0 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex min-w-40 items-center gap-2">
        <StatusChip tone={ENV_TONE[environment.type]}>{environment.name}</StatusChip>
        {environment.type === 'production' ? (
          environment.licenceActivationId ? (
            <Badge variant="success" title="This environment holds a licence activation slot">
              <ShieldCheckIcon className="size-3" />
              Licensed
            </Badge>
          ) : (
            <Badge variant="warning" title="No licence activation — the SDK will run degraded">
              Not activated
            </Badge>
          )
        ) : null}
      </div>

      <div className="min-w-0 flex-1">
        {environment.baseUrl ? (
          <a
            href={environment.baseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-data text-accent underline-offset-2 hover:underline"
          >
            {environment.baseUrl}
          </a>
        ) : (
          <span className="text-data text-ink-subtle">No base URL set</span>
        )}
        <p className="text-micro text-ink-subtle" data-numeric>
          Revision {environment.contentRevision} ·{' '}
          {environment.lastPublishedAt
            ? `last published ${formatDate(environment.lastPublishedAt)}`
            : 'never published'}
        </p>
      </div>

      <MaskedKey value={environment.publicKey} label="Public key" className="w-full sm:w-72" />
    </div>
  );
}

function SiteCard({
  orgId,
  site,
  detail,
}: {
  orgId: string;
  site: SiteWithEnvironments;
  detail: SiteDetail | undefined;
}) {
  const domains = detail?.domains ?? [];

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle>
            <Link
              to="/app/$orgId/sites/$siteId"
              params={{ orgId, siteId: site.id }}
              className="underline-offset-2 hover:underline"
            >
              {site.name}
            </Link>
          </CardTitle>
          <p className="truncate font-mono text-micro text-ink-subtle">{site.slug}</p>
          {site.description ? (
            <p className="mt-1 max-w-prose text-data text-ink-muted">{site.description}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {site.status === 'archived' ? <Badge variant="neutral">Archived</Badge> : null}
          <Button asChild variant="secondary" size="sm">
            <Link to="/app/$orgId/sites/$siteId" params={{ orgId, siteId: site.id }}>
              Open
            </Link>
          </Button>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col">
          {site.environments.map((environment) => (
            <EnvironmentRow key={environment.id} environment={environment} />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
          <span className="section-label">Domains</span>
          {domains.length === 0 ? (
            <span className="text-data text-ink-subtle">
              {detail ? 'None attached' : 'Loading…'}
            </span>
          ) : (
            domains.map((domain) => (
              <StatusChip
                key={domain.id}
                tone={
                  domain.verificationStatus === 'verified'
                    ? 'active'
                    : domain.verificationStatus === 'failed' ||
                        domain.verificationStatus === 'expired'
                      ? 'danger'
                      : 'pending'
                }
              >
                {domain.hostname}
              </StatusChip>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SitesPage() {
  const { orgId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const sites = useQuery(sitesQuery(orgId));

  // Domains and verification status only exist on the detail endpoint.
  const details = useQueries({
    queries: (sites.data ?? []).map((site) => siteDetailQuery(orgId, site.id)),
  });
  const detailById = new Map(
    details.flatMap((result) => (result.data ? [[result.data.id, result.data] as const] : [])),
  );

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      baseUrl: '',
      createStagingEnvironment: true,
    },
  });

  const createMutation = useMutation({
    mutationFn: (values: CreateValues) =>
      sitesApi.create(orgId, {
        name: values.name,
        ...(values.slug ? { slug: values.slug } : {}),
        ...(values.description ? { description: values.description } : {}),
        ...(values.baseUrl ? { baseUrl: values.baseUrl } : {}),
        createStagingEnvironment: values.createStagingEnvironment,
      }),
    onSuccess: async (site) => {
      toast.success(`${site.name} created`);
      setCreateOpen(false);
      form.reset();
      await queryClient.invalidateQueries({ queryKey: siteKeys.all(orgId) });
      void navigate({
        to: '/app/$orgId/sites/$siteId',
        params: { orgId, siteId: site.id },
      });
    },
    onError: (error) => toast.error(messageForError(error, 'That site could not be created.')),
  });

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Workspace"
        title="Sites"
        description="Every website this organisation manages. A site owns its environments, its public keys and the content the SDK serves."
        actions={
          <PermissionGate permission={PERMISSIONS.SITE_CREATE}>
            <Button onClick={() => setCreateOpen(true)}>
              <PlusIcon />
              Create site
            </Button>
          </PermissionGate>
        }
      />

      {sites.isLoading ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          {Array.from({ length: 2 }, (_, index) => (
            <Skeleton key={index} className="h-52 w-full rounded-lg" />
          ))}
        </div>
      ) : null}

      {sites.isError ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-data text-danger"
        >
          {messageForError(sites.error, 'The site list could not be loaded.')}
        </div>
      ) : null}

      {!sites.isLoading && !sites.isError && (sites.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={LayersIcon}
          title="No sites connected yet"
          description={
            <div className="flex flex-col gap-2">
              <p>
                A site is one website you already run. SiteFlow does not host it: you drop a script
                tag in, mark the regions editors may change with{' '}
                <code className="rounded-xs bg-sunken px-1 font-mono text-micro">
                  data-siteflow-key
                </code>
                , and the SDK swaps in the published copy at load.
              </p>
              <p>
                Each site gets a production environment and, optionally, a staging one so copy can
                be reviewed before it reaches visitors.
              </p>
            </div>
          }
          action={
            <PermissionGate
              permission={PERMISSIONS.SITE_CREATE}
              fallback={
                <p className="text-micro text-ink-subtle">
                  Ask an administrator to create the first site.
                </p>
              }
            >
              <Button onClick={() => setCreateOpen(true)}>
                <GlobeIcon />
                Connect your first site
              </Button>
            </PermissionGate>
          }
        />
      ) : null}

      <div className="flex flex-col gap-4">
        {(sites.data ?? []).map((site) => (
          <SiteCard key={site.id} orgId={orgId} site={site} detail={detailById.get(site.id)} />
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a site</DialogTitle>
            <DialogDescription>
              Nothing on the live website changes until you publish. Creating a site only sets up
              the environments and the keys the SDK will use.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              noValidate
              onSubmit={form.handleSubmit((values) => createMutation.mutate(values))}
            >
              <DialogBody className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Northbound Studio" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Slug</FormLabel>
                      <FormControl>
                        <Input placeholder="northbound-studio" className="font-mono" {...field} />
                      </FormControl>
                      <FormDescription>
                        Optional. Derived from the name when left empty.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="baseUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Production base URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://example.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        Where the production environment lives. Used for the install snippet and
                        licence activation.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea rows={2} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="createStagingEnvironment"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start gap-3">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="flex flex-col gap-0.5">
                        <FormLabel>Also create a staging environment</FormLabel>
                        <FormDescription>
                          Staging lets copy be reviewed on a non-public build first. It never
                          consumes a licence slot.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </DialogBody>

              <DialogFooter>
                <Button variant="secondary" onClick={() => setCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating…' : 'Create site'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/')({
  component: SitesPage,
});
